import { Box } from "@mui/material";

import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(4px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

export const FlowPanel = styled(Box)`
    position: relative;
    ${({ $bare }) =>
        $bare
            ? ""
            : `
        background: #ffffff;
        border: 1px solid rgba(0, 0, 0, 0.05);
        border-radius: 10px;
        padding: 28px 24px 32px;
    `}
    margin-bottom: 24px;
    animation: ${fadeIn} 320ms ease;

    ${({ $mobileFlush }) =>
        $mobileFlush &&
        `
        @media (max-width: 599.95px) {
            padding: 0;
        }
    `}
`;

const shimmer = keyframes`
    100% {
        transform: translateX(100%);
    }
`;

export const FlowPanelLoading = styled(Box)`
    position: relative;
    background: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.05);
    border-radius: 10px;
    padding: 28px 24px 32px;
    margin-bottom: 24px;
    display: flex;
    justify-content: space-between;
    gap: 32px;
`;

export const LoadingColumn = styled(Box)`
    flex: 0 1 270px;
    width: 270px;
    display: flex;
    flex-direction: column;
    gap: 10px;

    @media (max-width: 900px) {
        width: 100%;
        flex-basis: auto;
    }
`;

export const LoadingCard = styled(Box)`
    position: relative;
    height: 50px;
    border-radius: 10px;
    background: #f2f2f5;
    overflow: hidden;

    &::after {
        content: "";
        position: absolute;
        inset: 0;
        transform: translateX(-100%);
        background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.7),
            transparent
        );
        animation: ${shimmer} 1.4s ease-in-out infinite;
    }
`;

export const StatCardSkeleton = styled(Box)`
    position: relative;
    height: 112px;
    border-radius: 10px;
    border: 1px solid rgba(0, 0, 0, 0.05);
    background: #f2f2f5;
    overflow: hidden;

    &::after {
        content: "";
        position: absolute;
        inset: 0;
        transform: translateX(-100%);
        background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.7),
            transparent
        );
        animation: ${shimmer} 1.4s ease-in-out infinite;
    }
`;

export const FlowColumns = styled(Box)`
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 32px;

    @media (max-width: 900px) {
        flex-direction: column;
        align-items: stretch;
        gap: 32px;
    }
`;

export const FlowSvg = styled.svg`
    position: absolute;
    top: 0;
    left: 0;
    pointer-events: none;
    overflow: visible;

    @media (max-width: 900px) {
        display: none;
    }
`;

// Layered above the icon cards (which paint over the base FlowSvg by DOM
// order) but below LogoBadge's z-index:2, so a hover-only direct-to-hub
// line (see FlowDiagram.js) draws over the icons and tucks under the hub.
export const HoverFlowSvg = styled(FlowSvg)`
    z-index: 1;
`;

export const Column = styled(Box)`
    flex: 0 1 ${({ $width }) => $width || 270}px;
    width: ${({ $width }) => $width || 270}px;
    min-width: 0;
    position: relative;

    @media (max-width: 900px) {
        width: 100%;
        flex-basis: auto;
    }
`;

export const ColumnTitle = styled(Box)`
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: #86868b;
    font-family: "Nunito", sans-serif;
    margin-bottom: 14px;
    text-align: ${({ $align }) => $align || "left"};
`;

export const CardList = styled(Box)`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

export const ItemCard = styled(Box)`
    position: relative;
    background: #ffffff;
    border: 1px solid #e5e5ea;
    border-radius: 10px;
    overflow: hidden;
    transition: background 150ms ease;

    &:hover {
        background: #fafafa;
    }
`;

export const ItemHeader = styled(Box)`
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 50px;
    padding: 0 12px;
    box-sizing: border-box;
    cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
`;

export const IconChip = styled(Box)`
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: #f2f2f5;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    overflow: hidden;

    img {
        width: 17px;
        height: 17px;
        object-fit: contain;
    }
`;

export const ItemLabel = styled(Box)`
    flex: 1;
    min-width: 0;
    font-size: 14px;
    font-weight: 500;
    font-family: "Nunito", sans-serif;
    color: #1d1d1f;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const Badge = styled(Box)`
    background: #f2f2f5;
    color: #6e6e73;
    font-size: 12px;
    font-weight: 600;
    font-family: "Nunito", sans-serif;
    padding: 2px 9px;
    border-radius: 20px;
    flex-shrink: 0;
`;

export const ChevronButton = styled(Box)`
    cursor: pointer;
    color: #aeaeb2;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    transform: rotate(${({ $open }) => ($open ? 180 : 0)}deg);
    transition: transform 220ms ease;
`;

export const ItemDetail = styled(Box)`
    display: grid;
    grid-template-rows: ${({ $open }) => ($open ? "1fr" : "0fr")};
    opacity: ${({ $open }) => ($open ? 1 : 0)};
    transition:
        grid-template-rows 260ms ease,
        opacity 200ms ease;
`;

export const ItemDetailInner = styled(Box)`
    overflow: hidden;
`;

export const DetailRow = styled(Box)`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px 10px 60px;
    font-size: 13px;
    font-family: "Nunito", sans-serif;
    color: #444444;
    border-top: 1px solid #f0f0f2;
    box-sizing: border-box;
    cursor: pointer;

    &:hover {
        background: #f8f8f8;
    }
`;

export const DetailRowText = styled(Box)`
    flex: 1;
    min-width: 0;

    .tracker-name {
        font-size: 11px;
        color: #86868b;
    }

    .item-name {
        font-size: 13px;
        color: #1d1d1f;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

export const StatusDot = styled(Box)`
    position: absolute;
    top: 50%;
    ${({ $side }) => ($side === "right" ? "right: -5px;" : "left: -5px;")}
    transform: translateY(-50%);
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: ${({ $color }) => $color};
    border: 2px solid #ffffff;
`;

const dashFlow = keyframes`
    to {
        stroke-dashoffset: -20;
    }
`;

// Letting the browser interpolate the "d" attribute on toggle (instead of
// re-measuring every animation frame in JS) is what makes the reroute look
// animated — cheap because it's handled by the SVG/CSS engine, not React.
// Whether it's on is passed as a plain inline `style` prop from
// FlowDiagram.js rather than a styled-components dynamic prop: with many
// lines on screen, toggling a styled-components prop mints/injects a new
// class for every one of them on every toggle, which is the pricier path.
export const BasePath = styled.path``;

// Duration/delay also come from inline `style` (see FlowDiagram.js): each
// line has its own value, and threading that many distinct values through a
// styled-components prop mints one CSS class per distinct combination.
export const FlowPath = styled.path`
    animation-name: ${dashFlow};
    animation-timing-function: linear;
    animation-iteration-count: infinite;
    will-change: stroke-dashoffset;
`;

export const LogoBadge = styled(Box)`
    position: absolute;
    left: ${({ $x }) => $x}px;
    top: ${({ $y }) => $y}px;
    transform: translate(-50%, -50%);
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: #ffffff;
    border: 1px solid #e5e5ea;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
    cursor: ${({ $interactive }) => ($interactive ? "pointer" : "default")};

    ${({ $navigable }) =>
        $navigable
            ? `
    transition: transform 160ms ease, border-color 160ms ease,
        box-shadow 160ms ease;

    &:hover {
        transform: translate(-50%, -50%) scale(1.05);
        border-color: #d0d0d5;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    }
    `
            : ""}

    img {
        width: 60px;
        height: 60px;
        object-fit: contain;
    }

    @media (max-width: 900px) {
        display: none;
    }
`;

export const HubCaption = styled(Box)`
    position: absolute;
    left: ${({ $x }) => $x}px;
    top: ${({ $y }) => $y}px;
    margin-top: 40px;
    transform: translate(-50%, 0);
    font-size: 11px;
    color: #9a9aa0;
    font-family: "Nunito", sans-serif;
    white-space: nowrap;
    text-align: center;
    pointer-events: none;
    z-index: 2;

    @media (max-width: 900px) {
        display: none;
    }
`;

export const CompactCardList = styled(Box)`
    display: flex;
    flex-wrap: wrap;
    justify-content: ${({ $justify }) => $justify || "flex-start"};
    gap: 12px;
`;

export const CompactItemCard = styled(Box)`
    position: relative;
    width: 48px;
    height: 48px;
    border-radius: 10px;
    background: #ffffff;
    border: 1px solid #e5e5ea;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    transition:
        background 150ms ease,
        border-color 150ms ease;

    &:hover {
        background: #fafafa;
        border-color: #d0d0d5;
    }

    img {
        width: 24px;
        height: 24px;
        object-fit: contain;
    }
`;

export const CompactBadgeGroup = styled(Box)`
    position: absolute;
    top: -6px;
    right: -6px;
    display: flex;
    align-items: center;
    gap: 4px;
    z-index: 1;
`;

export const CompactCountBadge = styled(Box)`
    min-width: 18px;
    height: 18px;
    padding: 0 4px;
    box-sizing: border-box;
    border-radius: 9px;
    color: #ffffff;
    font-size: 10px;
    font-weight: 700;
    font-family: "Nunito", sans-serif;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid #ffffff;
    flex-shrink: 0;
`;

export const CompactSuccessBadge = styled(CompactCountBadge)`
    background: #45c300;
`;

export const CompactDangerBadge = styled(CompactCountBadge)`
    background: #d32f2f;
`;

export const CompactWarningBadge = styled(Box)`
    position: absolute;
    bottom: -6px;
    left: -6px;
    width: 18px;
    height: 18px;
    box-sizing: border-box;
    border-radius: 50%;
    background: #d32f2f;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid #ffffff;
    z-index: 1;
`;

export const CompactDetailPopover = styled(Box)`
    position: absolute;
    top: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    background: #ffffff;
    border: 1px solid #e5e5ea;
    border-radius: 10px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    min-width: 200px;
    z-index: 5;
    overflow: hidden;
`;

export const CompactDetailRow = styled(DetailRow)`
    padding: 10px 14px;
`;

export const AddCard = styled(Box)`
    background: #fafafa;
    border: 1px dashed #d5d5da;
    border-radius: 10px;
    padding: 16px;
    text-align: center;
`;

export const MobileTrackerList = styled(Box)`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const MobileSegmentedControl = styled(Box)`
    display: flex;
    width: 100%;
    padding: 0 16px;
    box-sizing: border-box;
    margin-bottom: 10px;
`;

export const MobileSegmentTab = styled(Box)`
    flex: 1 1 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #86868b;
    font-family: "Nunito", sans-serif;

    &:first-child {
        justify-content: flex-start;
    }

    &:last-child {
        justify-content: flex-end;
    }
`;

export const MobileTrackerCard = styled(Box)`
    background: #f7f7f8;
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 10px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    padding: 16px;
    box-sizing: border-box;
`;

export const MobileTrackerRow = styled(Box)`
    display: flex;
    align-items: flex-start;
    gap: 8px;
`;

export const MobileTrackerColumn = styled(Box)`
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    justify-content: ${({ $align }) =>
        $align === "left"
            ? "flex-start"
            : $align === "right"
              ? "flex-end"
              : "center"};
`;

export const MobileTrackerName = styled(Box)`
    text-align: center;
    font-size: 15px;
    font-weight: 700;
    font-family: "Nunito", sans-serif;
    color: #ed6c02;
    line-height: 1.25;
    overflow-wrap: break-word;
    cursor: pointer;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    overflow: hidden;
`;

export const MobileIconRow = styled(Box)`
    display: flex;
    flex-wrap: wrap;
    justify-content: ${({ $justify }) => $justify || "flex-start"};
    gap: 10px;
`;

export const MobileIconButton = styled(Box)`
    position: relative;
    width: 22px;
    height: 22px;
    border-radius: 6px;
    background: #ffffff;
    border: 1px solid #e5e5ea;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    cursor: pointer;

    img {
        width: 18px;
        height: 18px;
        object-fit: contain;
    }
`;

export const MobileEmptyHint = styled(Box)`
    font-size: 12px;
    color: #aeaeb2;
    font-family: "Nunito", sans-serif;
`;

export const MobileActionsRow = styled(Box)`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e5e5ea;
`;

export const MobileActionButton = styled(Box)`
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: #ffffff;
    border: 1px solid #e5e5ea;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #6e6e73;
    flex-shrink: 0;
    transition: background 150ms ease;

    &:hover {
        background: #fafafa;
    }
`;
